import {
  ORDER_STATUSES,
  DIAMOND_TYPES,
  CUSTOM_PRICE_TYPES,
  DEFAULT_CURRENCY,
  ORDER_MODES,
} from '../constants/appConstants';
import { getNonCertLotDisplayTitle, getNonCertOrderItemDisplayLabel } from './nonCertLotDisplay';
import { weightUnitShort } from './customItemPricing';
import { formatCurrency, getCurrencySymbol } from './currency';

export function resolveItemCurrencyCode(item) {
  return item?.currency || DEFAULT_CURRENCY;
}

/** True when ref is a populated subdocument (not a bare ObjectId string). */
function isPopulatedDoc(ref) {
  return ref != null && typeof ref === 'object' && !Array.isArray(ref);
}

function snapshotHasCertData(snap) {
  return isPopulatedDoc(snap)
    && (snap.certificateNumber || snap.shape || snap.carat != null);
}

/** Prefer embedded snapshot, then populated diamond ref — never a raw ObjectId. */
export function resolveCertifiedDiamondData(item) {
  if (snapshotHasCertData(item?.diamondSnapshot)) return item.diamondSnapshot;
  const d = item?.diamond;
  if (isPopulatedDoc(d) && (d.certificateNumber || d.shape || d.carat != null)) return d;
  if (isPopulatedDoc(item?.diamondSnapshot)) return item.diamondSnapshot;
  return null;
}

export function hasCertifiedDisplayData(item) {
  return resolveCertifiedDiamondData(item) != null;
}

/** Legacy SR fulfill rows stored as `certified` without diamond snapshot or description-only pricing. */
export function isLegacySpecialRequestStyleItem(item) {
  if (item?.type !== DIAMOND_TYPES.CERTIFIED) return false;
  if (hasCertifiedDisplayData(item)) return false;
  if (item?.description?.trim()) return true;
  if (item?.priceType === CUSTOM_PRICE_TYPES.FIXED) return true;
  if (item?.priceType === CUSTOM_PRICE_TYPES.BY_WEIGHT) return true;
  return false;
}

/** UI label type — may differ from persisted `item.type` for legacy special-request order items. */
export function getEffectiveItemDisplayType(item) {
  if (item?.type === DIAMOND_TYPES.CUSTOM) return DIAMOND_TYPES.CUSTOM;
  if (item?.type === DIAMOND_TYPES.NON_CERTIFIED) return DIAMOND_TYPES.NON_CERTIFIED;
  if (hasCertifiedDisplayData(item)) return DIAMOND_TYPES.CERTIFIED;
  if (isLegacySpecialRequestStyleItem(item)) return DIAMOND_TYPES.CUSTOM;
  return DIAMOND_TYPES.CERTIFIED;
}

export function getCertifiedCertificateNumberOrNull(item) {
  if (getEffectiveItemDisplayType(item) !== DIAMOND_TYPES.CERTIFIED) return null;
  return resolveCertifiedDiamondData(item)?.certificateNumber || null;
}

/** Prefer populated lot ref, then fall back when lot id is unpopulated. */
export function resolveLotData(item) {
  const lot = item?.lot;
  if (isPopulatedDoc(lot) && (lot.shape || lot.measurement)) return lot;
  return null;
}

function formatItemMoney(amount, item) {
  return formatCurrency(amount, resolveItemCurrencyCode(item));
}

export function getClientDisplayName(user) {
  if (!user) return '—';
  return `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email || '—';
}

export function getOrderId(item) {
  return item.order?._id ?? item.order;
}

/** Custom line item priced by weight (carat or gram); `fixed` custom items are excluded. */
export function isCustomByWeightItem(item) {
  return getEffectiveItemDisplayType(item) === DIAMOND_TYPES.CUSTOM
    && item?.priceType === CUSTOM_PRICE_TYPES.BY_WEIGHT;
}

export function getItemCarat(item) {
  const displayType = getEffectiveItemDisplayType(item);
  if (displayType === DIAMOND_TYPES.CUSTOM) {
    return isCustomByWeightItem(item) && item.weight != null ? Number(item.weight) : null;
  }
  if (displayType === DIAMOND_TYPES.NON_CERTIFIED) return item.weight;
  // Prefer the persisted weight (set at order time); fall back to live/snapshot carat
  // for legacy items created before the unified pricing migration.
  return item.weight ?? resolveCertifiedDiamondData(item)?.carat;
}

/** Certificate # for certified items (links, audit log titles). */
export function getCertifiedCertificateNumber(item) {
  const d = resolveCertifiedDiamondData(item);
  return d?.certificateNumber ?? '—';
}

/** Shape / color / clarity for certified items (description column; carat is in weight column). */
export function getCertifiedDiamondSpecs(item) {
  const d = resolveCertifiedDiamondData(item);
  if (!d) return '—';
  const specs = [d.shape, d.color, d.clarity].filter(Boolean).join(' ');
  return specs || '—';
}

/**
 * Shape / weight / color / clarity for certified items, weight shown inline right
 * after the shape. Used where the description itself should carry the weight
 * (Client Detail Items tab, Check Detail item rows) rather than relying solely on
 * an adjacent weight column/field.
 */
export function getCertifiedDiamondSpecsWithWeight(item) {
  const d = resolveCertifiedDiamondData(item);
  if (!d) return '—';
  const carat = getItemCarat(item);
  const weightLabel = carat != null ? `${carat} ct` : null;
  const specs = [d.shape, weightLabel, d.color, d.clarity].filter(Boolean).join(' ');
  return specs || '—';
}

/**
 * Order-items table "description" column: custom text, lot title, or certified specs.
 */
function getCustomItemDescriptionText(item) {
  const d = item.description != null ? String(item.description).trim() : '';
  return d || '—';
}

function getCustomItemPrimaryLabel(item) {
  const description = getCustomItemDescriptionText(item);
  if (description !== '—') return description;
  if (item.priceType === CUSTOM_PRICE_TYPES.BY_WEIGHT && item.weight != null) {
    const u = weightUnitShort(item.weightUnit);
    return `${Number(item.weight)} ${u}`;
  }
  if (item.finalPrice != null) return formatItemMoney(item.finalPrice, item);
  return '—';
}

export function getOrderItemDescriptionCell(item) {
  const displayType = getEffectiveItemDisplayType(item);
  if (displayType === DIAMOND_TYPES.CUSTOM) {
    return getCustomItemDescriptionText(item);
  }
  if (displayType === DIAMOND_TYPES.NON_CERTIFIED) {
    return getNonCertLotDisplayTitle(item.lot);
  }
  if (displayType === DIAMOND_TYPES.CERTIFIED) {
    return getCertifiedDiamondSpecs(item);
  }
  return '—';
}

/** Cert # for certified; description cell text for other types. */
export function getItemRef(item) {
  const displayType = getEffectiveItemDisplayType(item);
  if (displayType === DIAMOND_TYPES.CERTIFIED) {
    const cert = getCertifiedCertificateNumber(item);
    if (cert !== '—') return cert;
    const w = getItemCarat(item);
    if (w != null) return `${w} ct`;
    return '—';
  }
  if (displayType === DIAMOND_TYPES.CUSTOM) {
    return getCustomItemPrimaryLabel(item);
  }
  return getOrderItemDescriptionCell(item);
}

/** PDF/export description column: cert number plus specs for certified items. */
export function getItemExportDescription(item) {
  const displayType = getEffectiveItemDisplayType(item);
  if (displayType === DIAMOND_TYPES.CERTIFIED) {
    const cert = getCertifiedCertificateNumber(item);
    const specs = getCertifiedDiamondSpecs(item);
    if (specs && specs !== '—') {
      return `${cert} — ${specs}`;
    }
    return cert;
  }
  return getOrderItemDescriptionCell(item);
}

export function getItemLab(item) {
  return resolveCertifiedDiamondData(item)?.lab;
}

export function isNonCertByCountItem(item) {
  return item?.type === DIAMOND_TYPES.NON_CERTIFIED
    && item?.orderMode === ORDER_MODES.BY_COUNT
    && item?.stoneCount > 0;
}

/** Round carat weight to 2 decimal places (cart, checkout, and display). */
export function roundCarat2dp(value) {
  return Math.round((Number(value) || 0) * 100) / 100;
}

/**
 * True when a by-count item's weight still matches stoneCount × caratPerStone (not yet
 * updated by staff with the actual measured weight). Returns false when caratPerStone is
 * unavailable — we cannot determine estimated vs actual, so we don't show a false alert.
 */
export function isEstimatedNonCertWeight(item) {
  if (!isNonCertByCountItem(item)) return false;
  const cps = item.lot?.caratPerStone;
  if (!cps) return false;
  const expected = roundCarat2dp(item.stoneCount * cps);
  return Math.abs(roundCarat2dp(item.weight || 0) - expected) < 0.01;
}

export function getNonCertByCountStoneLabel(item, t) {
  if (!isNonCertByCountItem(item)) return null;
  return t('inventory.stoneCountValue', { count: item.stoneCount });
}

export function getNonCertWeightLabel(item, t) {
  if (!isNonCertByCountItem(item)) return null;
  const labelKey = isEstimatedNonCertWeight(item)
    ? 'inventory.estimatedWeight'
    : 'inventory.actualWeight';
  return `${t(labelKey)}: ${item.weight || 0} ct`;
}

export function getItemDetails(item, t = null) {
  const displayType = getEffectiveItemDisplayType(item);
  if (displayType === DIAMOND_TYPES.CUSTOM) {
    if (item.priceType === CUSTOM_PRICE_TYPES.BY_WEIGHT && item.weight != null) {
      const u = weightUnitShort(item.weightUnit);
      return `${Number(item.weight)} ${u}`;
    }
    if (item.priceType === CUSTOM_PRICE_TYPES.FIXED) return '—';
    return item.description || '—';
  }
  if (displayType === DIAMOND_TYPES.NON_CERTIFIED) {
    if (isNonCertByCountItem(item)) {
      if (t) return t('cart.stonesWeightEstimate', { count: item.stoneCount, weight: item.weight || 0 });
      return `${item.stoneCount} stones (~${item.weight || 0} ct)`;
    }
    return `${item.weight || 0} ct`;
  }
  if (displayType === DIAMOND_TYPES.CERTIFIED) {
    const w = getItemCarat(item);
    return w != null ? `${w} ct` : '—';
  }
  return '—';
}

export function getItemTypeLabel(item, t) {
  const displayType = getEffectiveItemDisplayType(item);
  if (displayType === DIAMOND_TYPES.CUSTOM) return t('cart.custom');
  if (displayType === DIAMOND_TYPES.NON_CERTIFIED) return t('cart.nonCertified');
  return t('cart.certified');
}

/**
 * Export/print "Type" column: certificate # for certified items (linking the row to
 * the physical stone), falling back to the type label for custom/non-certified items
 * which have no certificate number.
 */
export function getItemExportTypeLabel(item, t) {
  const displayType = getEffectiveItemDisplayType(item);
  if (displayType === DIAMOND_TYPES.CUSTOM) return t('cart.custom');
  if (displayType === DIAMOND_TYPES.NON_CERTIFIED) return t('cart.nonCertified');
  return getCertifiedCertificateNumber(item);
}

/**
 * Live-preview the displayed price for an OrderItem, taking pending in-form edits
 * into account so the user sees the new total before saving.
 *
 * `edits` may carry `weight` and/or `pricePerUnit` for non-certified items and
 * custom items priced by weight; certified items use `pricePerUnit` with persisted
 * carat weight. The unedited side falls back to the persisted `item.weight` /
 * `item.pricePerUnit`, and finally to deriving from `item.finalPrice` for legacy
 * items that don't carry the unified fields yet.
 */
export function getItemDisplayPrice(item, edits) {
  const carat = getItemCarat(item);
  const weightEditable = item.type === DIAMOND_TYPES.NON_CERTIFIED || isCustomByWeightItem(item);
  if (weightEditable && edits?.weight !== undefined && edits.weight !== '') {
    const w = Number(edits.weight);
    const ppuFromEdit = edits.pricePerUnit !== undefined && edits.pricePerUnit !== '' ? Number(edits.pricePerUnit) : null;
    const ppu = ppuFromEdit != null && !Number.isNaN(ppuFromEdit)
      ? ppuFromEdit
      : (item.pricePerUnit != null ? Number(item.pricePerUnit) : (carat > 0 && item.finalPrice != null ? item.finalPrice / Number(carat) : null));
    if (!Number.isNaN(w) && w > 0 && ppu != null && !Number.isNaN(ppu)) {
      return formatItemMoney(Math.round(ppu * w * 100) / 100, item);
    }
  }
  if (edits?.pricePerUnit !== undefined && edits.pricePerUnit !== '') {
    const ppu = Number(edits.pricePerUnit);
    if (!Number.isNaN(ppu) && carat != null && Number(carat) > 0) {
      return formatItemMoney(Math.round(ppu * Number(carat) * 100) / 100, item);
    }
  }
  return item.finalPrice != null ? formatItemMoney(item.finalPrice, item) : '—';
}

export function getItemPricePerCt(item) {
  const symbol = getCurrencySymbol(resolveItemCurrencyCode(item));
  const formatPpu = (ppu) => {
    const value = Number(ppu).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 });
    return `${symbol}${value}`;
  };
  if (item.pricePerUnit != null && item.pricePerUnit !== '') {
    const ppu = Number(item.pricePerUnit);
    if (!Number.isNaN(ppu)) return formatPpu(ppu);
  }
  const carat = getItemCarat(item);
  if (carat != null && carat > 0) {
    const ppu = item.finalPrice != null ? item.finalPrice / carat : null;
    if (ppu == null) return '—';
    return formatPpu(ppu);
  }
  return '—';
}

export function getItemStatusColorCashiering(status) {
  switch (status) {
    case ORDER_STATUSES.REQUESTED: return 'default';
    case ORDER_STATUSES.APPROVED: return 'info';
    case ORDER_STATUSES.READY: return 'primary';
    case ORDER_STATUSES.MEMO: return 'warning';
    case ORDER_STATUSES.PAID: return 'success';
    case ORDER_STATUSES.CANCELED: return 'error';
    default: return 'default';
  }
}

export function getItemStatusColorClientDetail(status) {
  switch (status) {
    case ORDER_STATUSES.REQUESTED: return 'warning';
    case ORDER_STATUSES.APPROVED: return 'info';
    case ORDER_STATUSES.READY: return 'primary';
    case ORDER_STATUSES.MEMO: return 'warning';
    case ORDER_STATUSES.PAID: return 'success';
    case ORDER_STATUSES.CANCELED: return 'default';
    case ORDER_STATUSES.EXPIRED: return 'default';
    default: return 'default';
  }
}

export function getItemStatusLabel(status, t) {
  const labels = {
    [ORDER_STATUSES.REQUESTED]: t('status.requested'),
    [ORDER_STATUSES.APPROVED]: t('status.approved'),
    [ORDER_STATUSES.READY]: t('status.ready'),
    [ORDER_STATUSES.CANCELED]: t('status.cancelled'),
    [ORDER_STATUSES.EXPIRED]: t('status.expired'),
    [ORDER_STATUSES.MEMO]: t('status.memo'),
    [ORDER_STATUSES.PAID]: t('status.paid'),
  };
  return labels[status] || status || '—';
}

/** Cashiering check-detail primary column label (cert / non-cert). */
export function getCashieringCheckItemPrimaryLabel(item) {
  const displayType = getEffectiveItemDisplayType(item);
  if (displayType === DIAMOND_TYPES.CERTIFIED) {
    const cert = getCertifiedCertificateNumber(item);
    const specs = getCertifiedDiamondSpecs(item);
    const w = getItemCarat(item);
    const weight = w != null ? `${w} ct` : '—';
    return `${cert} | ${specs} | ${weight}`;
  }
  if (displayType === DIAMOND_TYPES.NON_CERTIFIED) {
    return getNonCertOrderItemDisplayLabel(item);
  }
  if (displayType === DIAMOND_TYPES.CUSTOM) {
    return getItemRef(item);
  }
  return `Item #${item._id.slice(-6)}`;
}

/** Sort order items for PDF export (oldest first; missing dates last). */
export function sortOrderItemsByCreatedAt(items, direction = 'asc') {
  const list = [...(items || [])];
  const sign = direction === 'desc' ? -1 : 1;
  list.sort((a, b) => {
    const aTime = a?.createdAt ? new Date(a.createdAt).getTime() : null;
    const bTime = b?.createdAt ? new Date(b.createdAt).getTime() : null;
    if (aTime == null && bTime == null) return 0;
    if (aTime == null) return 1;
    if (bTime == null) return -1;
    return sign * (aTime - bTime);
  });
  return list;
}
